import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  isLoaded as isLoadedProducts,
  Plan,
  PlanModel,
  UnassignedPlanModel,
} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type HeartStrokeProps = {
  productType: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  riderCodeList: I.List<string>;
  riderAmounts: I.List<number>;
  productList: PlanModel;
  overrideBillForm: boolean;
  baseUnits: StringUnion;
};

export class InvalidGroupHeartStroke {
  public readonly key = 'InvalidGroupHeartStroke';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly productList: PlanModel,
    public readonly overrideBillForm: boolean,
    public readonly baseUnits: StringUnion
  ) {}
}

export class ValidGroupHeartStroke {
  public static createApp({
    productType,
    planType,
    premium,
    calculatedPremium,
    cwa,
    section125,
    replacement,
    policyNumber,
    riderCodeList,
    riderAmounts,
    productList,
    overrideBillForm,
    baseUnits,
  }: HeartStrokeProps) {
    return this.create(
      productType,
      planType,
      premium,
      calculatedPremium,
      cwa,
      section125,
      replacement,
      policyNumber,
      riderCodeList,
      riderAmounts,
      productList,
      overrideBillForm,
      baseUnits
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    riderCodeList: I.List<string>,
    riderAmounts: I.List<number>,
    productList: PlanModel,
    overrideBillForm: boolean,
    baseUnits: StringUnion
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      section125 &&
      replacement &&
      policyNumber &&
      riderCodeList.size > 0 &&
      riderAmounts.size > 0 &&
      isLoadedProducts(productList)
      ? new ValidGroupHeartStroke(
          productType,
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          replacement,
          policyNumber,
          riderCodeList,
          riderAmounts,
          productList,
          overrideBillForm,
          baseUnits
        )
      : new InvalidGroupHeartStroke(
          productType,
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          replacement,
          policyNumber,
          riderCodeList,
          riderAmounts,
          productList,
          overrideBillForm,
          baseUnits
        );
  }

  public readonly key = 'ValidGroupHeartStroke';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly productList: Loaded<Plan[]>,
    public readonly overrideBillForm: boolean,
    public readonly baseUnits: StringUnion
  ) {}
}

export type GroupHeartStroke = InvalidGroupHeartStroke | ValidGroupHeartStroke;

export type GroupHeartStrokeOptions<TReturn> = ToPartialMatchOptions<GroupHeartStroke, 'key', TReturn>;

export const matchGroupHeartStroke = <TReturn>(options: GroupHeartStrokeOptions<TReturn>) => (
  app: GroupHeartStroke
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupHeartStroke':
      return options.InvalidGroupHeartStroke ? options.InvalidGroupHeartStroke(app) : options.default(app);
    case 'ValidGroupHeartStroke':
      return options.ValidGroupHeartStroke ? options.ValidGroupHeartStroke(app) : options.default(app);
  }
};

export const EmptyGroupHeartStroke = new InvalidGroupHeartStroke(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  I.List<string>(),
  I.List<number>(),
  new UnassignedPlanModel(),
  false,
  new EmptyString()
);

export const changeProductType = (productType: string) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, productType: NonEmptyString.create(productType)});

export const changePlanType = (planType: string) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, premium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeSection125 = (section125: boolean) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeRiderCodes = (riderCode: string, index: number) => (app: GroupHeartStroke) => {
  const existingList: I.List<string> = app.riderCodeList;
  const newList = existingList.set(index, riderCode);
  return ValidGroupHeartStroke.createApp({...app, riderCodeList: newList});
};

export const changeRiderAmount = (riderAmt: number, index: number) => (app: GroupHeartStroke) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);

  return ValidGroupHeartStroke.createApp({...app, riderAmounts: newList});
};

export const changeProductList = (products: PlanModel) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, productList: products});

export const changeOverrideBillForm = (overrideBillForm: boolean) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, overrideBillForm});

export const changeBaseUnits = (baseUnits: string) => (app: GroupHeartStroke) =>
  ValidGroupHeartStroke.createApp({...app, baseUnits: NonEmptyString.create(baseUnits)});
